import { format } from 'date-fns';
import { getDealComponentsSafe, updateAmountOnItem, getId } from './common';

const getDealTransactionType = voucherTypeId => {
  if (voucherTypeId === 2 || voucherTypeId === 4) {
    return 'AP';
  }

  if (voucherTypeId === 1 || voucherTypeId === 3) {
    return 'AR';
  }

  throw new Error('I DUNNO');
};

const getOrCreateDeal = deals => {
  // TODO: is there more to consider?
  if (deals.length === 0) {
    const newDeal = {
      DealId: getId(),
      Pending: true,
      // TODO: everything below here is proabaly wrong
      ContractDate: format(new Date(), 'YYYY-MM-DD'),
      TransactionDate: format(new Date(), 'YYYY-MM-DD'),
      BillableDate: format(new Date(), 'YYYY-MM-DD'),
      PayableDate: format(new Date(), 'YYYY-MM-DD'),
      StatusId: 0,
    };
    deals.push(newDeal);
    return newDeal;
  }

  // Return the first deal
  return deals[0];
};

const isVoucherTransactionMapped = (
  voucherTransactionId,
  voucherTransactionAmount,
  dtvts,
) => {
  const relatedDtvts = dtvts.filter(
    ({ VoucherTranId }) => VoucherTranId === voucherTransactionId,
  );

  const relatedDtvtsAmountSum = relatedDtvts.reduce(
    (sum, { Amount }) => Amount + sum,
    0,
  );

  return relatedDtvtsAmountSum === voucherTransactionAmount;
};

// A deal transactions is editable if the credit and debit accounts match
// AND IF the deal transaction has a dtvt that it has only 1 dtvt and that the voucher transaction
// Id on the dtvt is the voucher transaction that is editing it
// OR if the deal transaction has no dtvts
const getDealTransactionsThatAreEditableByVoucherTransaction = (
  voucher,
  voucherTransaction,
  dtvts,
  dealTransactions,
) => {
  return dealTransactions.filter(dt => {
    let isDealTransactionEditable = true;

    // filter out deal transaction if accounts are incorrect
    const dealTransactionType = getDealTransactionType(voucher.VoucherTypeId);
    if (dealTransactionType === 'AR') {
      if (
        dt.DebitAccount !== voucher.AccountId ||
        dt.CreditAccount !== voucherTransaction.AccountId
      )
        isDealTransactionEditable = false;
    } else if (dealTransactionType === 'AP') {
      if (
        dt.DebitAccount !== voucherTransaction.AccountId ||
        dt.CreditAccount !== voucher.AccountId
      )
        isDealTransactionEditable = false;
    }

    const dtvtsOnDealTransaction = dtvts.filter(
      ({ DealTranId }) => dt.DealTranId === DealTranId,
    );
    if (dtvtsOnDealTransaction.length > 1) {
      // filter out deal transaction if it has more than 1 dtvt
      isDealTransactionEditable = false;
    } else if (dtvtsOnDealTransaction.length === 1) {
      // filter out deal transactions with 1 dtvt where the dtvt's voucher tran id is different
      // than the voucher tran id trying to edit it
      const dtvtOnDealTransaction = dtvtsOnDealTransaction[0];
      if (
        dtvtOnDealTransaction.VoucherTranId !== voucherTransaction.VoucherTranId
      )
        isDealTransactionEditable = false;
    }

    return isDealTransactionEditable;
  });
};

// Get deal transactions that map to our voucher transaction
const getMappedDealTransactions = (
  voucherTransactionId,
  dealTransactions,
  dtvts,
) =>
  // filter out deal transactions that do not map to voucher transaction
  dealTransactions.filter(
    dt =>
      // get dtvt linking deal transaction and voucher transaction, if it does not exist return
      // false
      !!dtvts.find(
        ({ DealTranId, VoucherTranId }) =>
          voucherTransactionId === VoucherTranId &&
          DealTranId === dt.DealTranId,
      ),
  );

// An voucher transaction is mappable if it has 1 or less dtvts and the voucher isEditable is true
const isVoucherTransactionMappable = (voucherTransactionId, voucher, dtvts) => {
  // If the voucher is not editable we can't map to it
  // TODO: waiting for sproc IMPORTANT
  // TODO: SERIOUSLY DONT FORGET THIS
  // if (!voucher.IsEditable) return false;

  const voucherTransactionDtvts = dtvts.filter(
    ({ VoucherTranId }) => VoucherTranId === voucherTransactionId,
  );

  // If there is 1 or less dtvts on the voucher transaction we can map to it
  return voucherTransactionDtvts.length <= 1;
};

const getDealTransactionsByAmount = (
  voucherTransactionAmount,
  dealTransactions,
) => {
  return dealTransactions.filter(dt => dt.Amount === voucherTransactionAmount);
};

const mapVouchers = dealMap => {
  const {
    deals,
    dealTransactions,
    dtvts,
    voucherTransactions,
    vouchers,
  } = getDealComponentsSafe(dealMap);

  const { DealId } = getOrCreateDeal(deals);

  voucherTransactions.forEach(voucherTransaction => {
    const {
      VoucherTranId,
      CategoryId,
      AccountId: voucherTransactionAccountId,
      Amount,
      VoucherId,
    } = voucherTransaction;
    // Get voucher on voucher transaction
    const voucherOnVoucherTransaction = vouchers.find(
      i => i.VoucherId === VoucherId,
    );

    // Get deal transactions on deal
    const dealTransactionsOnDeal = dealTransactions.filter(
      d => DealId === d.DealId,
    );

    const PendingDealTransactionType = getDealTransactionType(
      voucherOnVoucherTransaction.VoucherTypeId,
    );

    // If voucher is already mapped we can skip it
    if (isVoucherTransactionMapped(VoucherTranId, Amount, dtvts)) return;
    // If the voucher transaction is not mappable we can skip it
    if (
      !isVoucherTransactionMappable(
        VoucherTranId,
        voucherOnVoucherTransaction,
        dtvts,
      )
    )
      return;
    // Get all deal transactions that can be edited by this voucher transaction
    const editableDealTransactions = getDealTransactionsThatAreEditableByVoucherTransaction(
      voucherOnVoucherTransaction,
      voucherTransaction,
      dtvts,
      dealTransactionsOnDeal,
    );

    // Get all editable deal transactions that are already mapped to our voucher transaction
    const mappedEditableDealTransactions = getMappedDealTransactions(
      VoucherTranId,
      editableDealTransactions,
      dtvts,
    );

    if (mappedEditableDealTransactions.length >= 1) {
      // if we have a mapped editable deal transaction modify the dtvt amount and the
      // voucher transaction amount, just take first one
      const dealTransactionToEdit = mappedEditableDealTransactions[0];
      // TODO: extract function for finding dtvts by deal transaction id. Should throw error if
      //  more than 1 found (maybe?)
      const dtvtToEdit = dtvts.find(
        dt => dt.DealTranId === dealTransactionToEdit.DealTranId,
      );
      updateAmountOnItem(dealTransactionToEdit, Amount);
      updateAmountOnItem(dtvtToEdit, Amount);
    } else {
      const editableDealTransactionsWithSameAmount = getDealTransactionsByAmount(
        Amount,
        editableDealTransactions,
      );

      if (editableDealTransactionsWithSameAmount.length >= 1) {
        // if we have an editalbe deal transaction with the same amount we can map to it
        const dealTransactionToMapTo =
          editableDealTransactionsWithSameAmount[0];
        // create mapping
        dtvts.push({
          DealTranId: dealTransactionToMapTo.DealTranId,
          VoucherTranId,
          Amount,
          Pending: true,
        });
      } else if (editableDealTransactions.length > 1) {
        // if there are 1 or more editable deal transactions
        // map to the first editable deal transaction
        const dealTransactionToMapTo = editableDealTransactions[0];
        // create mapping
        dtvts.push({
          DealTranId: dealTransactionToMapTo.DealTranId,
          VoucherTranId,
          Amount,
          Pending: true,
        });
      } else {
        // TODO: refactor this
        const dealTransactionToMapTo = {
          Pending: true,
          DealTranId: getId(),
          ContactId: voucherOnVoucherTransaction.ContactId,
          DealId,
          CategoryId,
          Amount,
          // TODO: this is wrong
          Quantity: 1,
          // TODO: holy crap fix this it shouldn't default to 1
          Category: voucherTransaction.CategoryId || 1,
          CreditAccount:
            PendingDealTransactionType === 'AP'
              ? voucherOnVoucherTransaction.AccountId
              : voucherTransactionAccountId,
          DebitAccount:
            PendingDealTransactionType === 'AP'
              ? voucherTransactionAccountId
              : voucherOnVoucherTransaction.AccountId,
          TranType: PendingDealTransactionType,
        };
        dealTransactions.push(dealTransactionToMapTo);
        dtvts.push({
          DealTranId: dealTransactionToMapTo.DealTranId,
          VoucherTranId,
          Amount,
          Pending: true,
        });
      }
    }
  });

  return {
    deals,
    dealTransactions,
    dtvts,
    voucherTransactions,
    vouchers,
  };
};

export { mapVouchers };
